import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Check, Minus, X } from 'lucide-react';
import { SiteHeader } from '../../components/SiteHeader';
import { SiteFooter } from '../../components/SiteFooter';
import { Reveal } from '../../components/landing/Reveal';
import { fadeUpSm, staggerContainer, useRevealProps } from '../../components/landing/motion';
import { useDocumentMeta } from '../../lib/useDocumentMeta';

type Cell = 'yes' | 'partial' | 'no';

interface Row {
  labelKey: string;
  noteKey?: string;
  plyez: Cell;
  bakuraku: Cell;
  ramp: Cell;
  bill: Cell;
  tipalti: Cell;
}

// Honest capability matrix — mirrors the landing Comparison verdicts.
// Bakuraku is a strong JP-domestic peer (✓ on qualified-invoice, OCR,
// e-bookkeeping, ringi, Zengin, PINT send); PlyEZ differentiates on
// cross-border, AI transparency, public pricing, double-entry, per-rate
// tax split and budgets. No stale "model picker" row.
const ROWS: Row[] = [
  { labelKey: 'compare.rows.jpQualifiedInvoice', plyez: 'yes', bakuraku: 'yes', ramp: 'no', bill: 'no', tipalti: 'no' },
  { labelKey: 'compare.rows.docOcr', plyez: 'yes', bakuraku: 'yes', ramp: 'no', bill: 'no', tipalti: 'no' },
  { labelKey: 'compare.rows.jpEBookkeeping', plyez: 'yes', bakuraku: 'yes', ramp: 'no', bill: 'no', tipalti: 'partial' },
  { labelKey: 'compare.rows.ringi', plyez: 'yes', bakuraku: 'yes', ramp: 'no', bill: 'no', tipalti: 'no' },
  { labelKey: 'compare.rows.zengin', plyez: 'yes', bakuraku: 'yes', ramp: 'no', bill: 'no', tipalti: 'no' },
  { labelKey: 'compare.rows.threeWayMatch', plyez: 'yes', bakuraku: 'yes', ramp: 'partial', bill: 'yes', tipalti: 'yes' },
  { labelKey: 'compare.rows.peppolSend', plyez: 'yes', bakuraku: 'yes', ramp: 'no', bill: 'no', tipalti: 'partial' },
  { labelKey: 'compare.rows.peppolReceive', plyez: 'partial', bakuraku: 'partial', ramp: 'no', bill: 'no', tipalti: 'partial' },
  { labelKey: 'compare.rows.crossBorder', plyez: 'yes', bakuraku: 'no', ramp: 'yes', bill: 'partial', tipalti: 'yes' },
  { labelKey: 'compare.rows.multiCurrency', plyez: 'yes', bakuraku: 'no', ramp: 'yes', bill: 'partial', tipalti: 'yes' },
  { labelKey: 'compare.rows.usTax', plyez: 'yes', bakuraku: 'no', ramp: 'partial', bill: 'yes', tipalti: 'yes' },
  { labelKey: 'compare.rows.euVat', plyez: 'yes', bakuraku: 'no', ramp: 'partial', bill: 'partial', tipalti: 'yes' },
  { labelKey: 'compare.rows.arCollections', plyez: 'yes', bakuraku: 'yes', ramp: 'no', bill: 'yes', tipalti: 'no' },
  { labelKey: 'compare.rows.doubleEntry', plyez: 'yes', bakuraku: 'partial', ramp: 'no', bill: 'no', tipalti: 'no' },
  { labelKey: 'compare.rows.perRateTax', plyez: 'yes', bakuraku: 'partial', ramp: 'no', bill: 'no', tipalti: 'no' },
  { labelKey: 'compare.rows.budgets', plyez: 'yes', bakuraku: 'partial', ramp: 'yes', bill: 'no', tipalti: 'no' },
  { labelKey: 'compare.rows.aiTransparency', plyez: 'yes', bakuraku: 'no', ramp: 'no', bill: 'no', tipalti: 'no' },
  { labelKey: 'compare.rows.publicPricing', plyez: 'yes', bakuraku: 'no', ramp: 'partial', bill: 'partial', tipalti: 'no' },
  { labelKey: 'compare.rows.selfServe', plyez: 'yes', bakuraku: 'no', ramp: 'yes', bill: 'yes', tipalti: 'no' },
  { labelKey: 'compare.rows.soc2', noteKey: 'compare.rows.soc2Note', plyez: 'partial', bakuraku: 'partial', ramp: 'yes', bill: 'yes', tipalti: 'yes' },
  { labelKey: 'compare.rows.supplierNetwork', noteKey: 'compare.rows.supplierNetworkNote', plyez: 'no', bakuraku: 'no', ramp: 'partial', bill: 'yes', tipalti: 'yes' },
];

const COLS = ['capability', 'plyez', 'bakuraku', 'ramp', 'bill', 'tipalti'] as const;

export function ComparePage() {
  const { t } = useTranslation();
  useDocumentMeta({
    title: 'Compare — PlyEZ vs Bakuraku, Ramp, Bill.com, Tipalti, Brex',
    description: 'How PlyEZ stacks up against LayerX Bakuraku, Ramp, Bill.com, Tipalti, and Brex on JP compliance (適格請求書/電帳法/全銀/JP PINT) + cross-border payables + public pricing.',
    path: '/compare',
  });
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <section data-landing className="washi">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
          <Reveal as="div" className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
              <span className="sparkle-dot" aria-hidden />
              {t('compare.eyebrow')}
            </span>
            <h1 className="display text-4xl lg:text-5xl font-semibold mt-4">{t('compare.title')}</h1>
            <p className="mt-3 text-muted-foreground">{t('compare.subtitle')}</p>
          </Reveal>

          <div className="mt-10 overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
            <table className="w-full text-sm">
              <caption className="sr-only">{t('compare.title')}</caption>
              <thead>
                <tr className="bg-muted/60 text-left">
                  {COLS.map((col, i) => (
                    <th
                      key={col}
                      scope="col"
                      className={`px-4 py-3 font-medium ${i === 1 ? 'text-accent-700' : 'text-muted-foreground'}`}
                    >
                      {t(`compare.columns.${col}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <motion.tbody variants={staggerContainer} {...useRevealProps()}>
                {ROWS.map((row) => (
                  <motion.tr key={row.labelKey} variants={fadeUpSm} className="border-t border-border">
                    <th scope="row" className="px-4 py-3 text-left font-normal text-foreground">
                      <div>{t(row.labelKey)}</div>
                      {row.noteKey ? <div className="text-xs text-muted-foreground mt-1">{t(row.noteKey)}</div> : null}
                    </th>
                    <CellIcon value={row.plyez} colKey="plyez" highlighted />
                    <CellIcon value={row.bakuraku} colKey="bakuraku" />
                    <CellIcon value={row.ramp} colKey="ramp" />
                    <CellIcon value={row.bill} colKey="bill" />
                    <CellIcon value={row.tipalti} colKey="tipalti" />
                  </motion.tr>
                ))}
              </motion.tbody>
            </table>
          </div>

          <div className="rule mt-8" />
          <p className="text-xs text-muted-foreground mt-6 max-w-3xl">{t('compare.footnote')}</p>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}

function CellIcon({ value, colKey, highlighted }: { value: Cell; colKey: string; highlighted?: boolean }) {
  const { t } = useTranslation();
  const label = t(`compare.columns.${colKey}`) + ': ' + t(`compare.cell.${value}`);
  return (
    <td className={`px-4 py-3 ${highlighted ? 'bg-cinnabar-500/[0.06] dark:bg-cinnabar-500/[0.12]' : ''}`}>
      {value === 'yes' ? (
        <Check size={18} className="text-emerald-600" aria-label={label} />
      ) : value === 'partial' ? (
        <Minus size={18} className="text-warn-600" aria-label={label} />
      ) : (
        <X size={18} className="text-ink-300" aria-label={label} />
      )}
    </td>
  );
}
